import { Feature, Geometry } from "geojson";
import { LatLngExpression, LeafletMouseEvent, PathOptions } from "leaflet";
import { Dispatch, useEffect, useState } from 'react';
import { GeoJSON, MapContainer, Pane, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { useQuery } from 'react-query';
import { HUC_6 } from '../assets';
import { useSearchContextContainer } from '../hooks/SearchProvider';
import { getGeocodeUrl, NominatimSearchResponse } from '../utilities/nominatim';
import { HucFeatureProperties, useFetchHucs } from './../hooks/useFetchHucs';
import Huc10s from "./Huc10s";
import { HucDrawerChildren } from "./HucDrawerChildren";
import PersistentDrawerLeft from './PersistentDrawerLeft';

const position: LatLngExpression = [38.0902, -95.713]

const MAP_ID: string = "id"

interface MapHandlerProps {
  clearSelection: Dispatch<void>
}

export interface Location {
  name: string
  lat: number
  lon: number
}

const handleSelect = (data: NominatimSearchResponse) => {
  if (data.length > 0) {
    const { lat, lon, display_name } = data[0]
    return { lat: parseFloat(lat), lon: parseFloat(lon), name: display_name } as Location
  }
  return undefined
}

function MapSearchHandler() {
  const map = useMap()
  const locationSearchName = useSearchContextContainer()
  const locationSearchNameExists = locationSearchName.length > 0

  const { data: location } = useQuery(['nominatim_geolocation', locationSearchName], async () => {
    const url = getGeocodeUrl(locationSearchName[0])
    const res = await fetch(url)
    return await res.json() as Promise<NominatimSearchResponse>
    // TODO: split logic out into a class for handling NominatimSearchResponse's
  },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: locationSearchNameExists,
      select: handleSelect,
      onError: console.error,
    }
  )

  useEffect(() => {
    location && map.setView({ lat: location?.lat, lng: location?.lon }, 10)
  }, [locationSearchName, location, map])

  return null
}

function MapHandlers({ clearSelection }: MapHandlerProps) {
  useMapEvents({
    click: (e) => {
      // @ts-ignore
      const id: string = e.originalEvent.target.id
      id === MAP_ID && clearSelection()
    }
  })

  return null
}

export function styleHucFeatures(hucId: string | undefined, feature: Feature<Geometry, HucFeatureProperties> | undefined): PathOptions {
  // band aid fix. needs to be changed later
  let opacity = 0.1
  const featHucId = feature?.properties.hucId
  if (featHucId !== undefined && hucId !== undefined) {
    if (featHucId === hucId.slice(0, featHucId.length)) {
      opacity = 0.4
    }
  }

  const style: PathOptions = {
    color: "#3D624C",
    stroke: true,
    weight: 2,
    fillOpacity: opacity
  }
  return style
}

export default function Map() {
  console.log("rendered")

  const { hucs: data } = useFetchHucs(HUC_6)
  // TODO: create interface for huc geojson data and hooks interaction
  const [featureProps, setFeatureProps] = useState<HucFeatureProperties | undefined>();
  const [huc6, setHuc6] = useState<Feature>()

  // const [huc10Props, setHuc10Props] = useState<Feature<Geometry, GeoJsonProperties> | undefined>()

  function clearSelection() {
    // clears map selection
    setFeatureProps(undefined)
    setHuc6(undefined)
  }

  function handleClick(event: LeafletMouseEvent) {
    setHuc6(event.propagatedFrom.feature)
    // remove from map on click
    setFeatureProps(event.propagatedFrom.feature.properties)
  }

  return (
    <>
      {/* Drawer that opens when polygon has been selected on the map */}
      <PersistentDrawerLeft open={!!featureProps} opener={(b: boolean) => clearSelection()}>
        {/* can use non-null assertion b.c. never will be open unless featureProps is present */}
        {/* {huc10Props ? huc10Props) } : <HucDrawerChildren {...featureProps!} />} */}
        < HucDrawerChildren {...featureProps!} />
      </PersistentDrawerLeft>

      < MapContainer id={MAP_ID} center={position} zoom={5} style={{ height: "100%", width: "100wh" }}>

        <Pane name="basemap" style={{ zIndex: 0 }} >
          <TileLayer
            // attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            // url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" // open street maps xyz tile
            // url="http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}" // google terrain xyz tile
            url="http://mt0.google.com/vt/lyrs=p&hl=en&x={x}&y={y}&z={z}" // google hybrid xyz tile layer
            noWrap={true}
          />
        </Pane>

        <Pane name="huc6" style={{ zIndex: 1 }} >
          {data && <GeoJSON data={data} style={feature => styleHucFeatures(featureProps?.hucId, feature)}
            eventHandlers={{
              click: handleClick
            }}
          />
          }
        </Pane>

        <Pane name="huc10" style={{ zIndex: 2 }} >
          <Huc10s parentHuc={huc6} setProps={setFeatureProps} />
        </Pane>


        <MapHandlers clearSelection={clearSelection} />
        <MapSearchHandler />
      </MapContainer >
    </>
  )
}
